import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { atomicConfig } from "../../../../../config";
import { forEachWithConcurrency } from "../../../../../utils/concurrency";
import { call_api_auth } from "../../../../../utils/services";
import {
  AGENT_INFO,
  INTERNAL_STORES,
  compareFionaSavings,
  decisionHasActiveDeal,
  getAgentKey,
  getDealKey,
  isFionaDecision,
  isRossRevertDecision,
} from "../../constants";
import MultiDecisionAccordion from "../StoreProductsAnalysis/MultiDecisionAccordion";
import { useStores } from "../shared/Storepicker";

import {
  RECOMMENDATION_TABS,
  getRecommendationTab,
} from "./recommendationTabs";

const PLACEHOLDER_IMAGE =
  "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

const AMAZON_DOMAINS = {
  us: "www.amazon.com",
  ca: "www.amazon.ca",
  mx: "www.amazon.com.mx",
};
const getAmazonUrl = (asin, marketplace) => {
  const domain =
    AMAZON_DOMAINS[(marketplace || "us").toLowerCase()] || "www.amazon.com";
  return `https://${domain}/dp/${asin}`;
};

// Each per-store request is its own Lambda run, and "All stores" for a
// superadmin spans every active store, so only a few run at once.
const DECISIONS_CONCURRENCY = 6;

// Internal stores carry most recommendations and free-tier stores currently
// none, so internal stores are requested first; the rest keep picker order.
const byFetchPriority = (a, b) =>
  INTERNAL_STORES.includes(b.name) - INTERNAL_STORES.includes(a.name);

async function fetchDecisionsForStore(store, marketplace) {
  const url = `${atomicConfig.storeManagementServiceUrl}/rule-engine-decisions/${store}?marketplace=${marketplace}`;
  const result = await call_api_auth(url, "GET");
  return result.data;
}

async function fetchProductItemsByAsins(asins, store) {
  if (!asins || asins.length === 0) return {};
  const url = `${atomicConfig.storeManagementServiceUrl}/fetch-product-items-by-asins/${store}`;
  try {
    const result = await call_api_auth(url, "POST", JSON.stringify({ asins }));
    const items = result.data?.product_items || [];
    const map = {};
    items.forEach((item) => {
      if (item.asin) map[item.asin] = item;
    });
    return map;
  } catch {
    return {};
  }
}

// A Fiona card is one ASIN, store and card group (today's or next fee
// schedule's); its decisions' execution_id can repeat across them
const fionaCardKey = (d) =>
  `${d.storeName}|${d.asin}|${d.decision?.card_group || "fiona"}`;
const fionaProductKey = (d) => `${d.storeName}|${d.asin}`;

const MAX_CARD_WIDTH = 820;

const ProductInfoPanel = ({ productInfo, asin, storeName, marketplace }) => {
  if (!productInfo) {
    return (
      <Box
        sx={{
          width: 160,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={PLACEHOLDER_IMAGE}
          alt={asin}
          style={{
            width: 56,
            height: 56,
            borderRadius: 8,
            objectFit: "cover",
            opacity: 0.5,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 160,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.75,
        py: 1,
        borderRight: "1px solid #f0f0f0",
        pr: 2,
      }}
    >
      <Tooltip title="View on Amazon" arrow>
        <Link
          href={getAmazonUrl(asin, marketplace)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={productInfo.image || PLACEHOLDER_IMAGE}
            alt={productInfo.product_name || asin}
            style={{
              width: 64,
              height: 64,
              borderRadius: 8,
              objectFit: "cover",
            }}
          />
        </Link>
      </Tooltip>
      <Tooltip title={productInfo.product_name || ""} placement="bottom">
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            textAlign: "center",
            lineHeight: 1.2,
            maxWidth: 150,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            fontSize: "0.7rem",
            color: "#333",
          }}
        >
          {productInfo.product_name || asin}
        </Typography>
      </Tooltip>
      {productInfo.sku && (
        <Typography
          variant="caption"
          sx={{ color: "#999", fontSize: "0.65rem" }}
        >
          SKU: {productInfo.sku}
        </Typography>
      )}
      {Number(productInfo.monthly_sold) > 0 && (
        <Typography
          variant="caption"
          sx={{ color: "#666", fontSize: "0.65rem" }}
        >
          {Number(productInfo.monthly_sold).toLocaleString()} sold/mo
        </Typography>
      )}
      <Tooltip title="View product in Store Analysis" arrow>
        <Link
          href={`/store-products-analysis?store=${storeName}&asin=${asin}&marketplace=${
            marketplace || "us"
          }`}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{ fontSize: "0.65rem", cursor: "pointer" }}
        >
          View in Store Analysis
        </Link>
      </Tooltip>
    </Box>
  );
};

export default function AgentRecommendations({ store, marketplace }) {
  const [decisions, setDecisions] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agentFilter, setAgentFilter] = useState("all");
  // The tab over every agent's cards: "open" (Needs decision), "in_progress"
  // or "fyi"
  const [tab, setTab] = useState("open");
  // What people did to Fiona cards since the fetch, applied over `decisions`
  // (which the fetch keeps rewriting while stores load): by fionaCardKey, the
  // card's new fiona_card or { removed: true }; by fionaProductKey,
  // { removed: true } when a reject took all the product's cards.
  const [fionaChanges, setFionaChanges] = useState({});
  const [removedExecutionIds, setRemovedExecutionIds] = useState(new Set());
  // Dave's deal fields on a decision don't change when the user rejects his
  // offer locally — track it here so the agent tab filters/counts drop the
  // decision from "Dave" immediately instead of waiting on a refetch. Keyed
  // by getDealKey rather than execution_id, which isn't guaranteed unique
  // per decision (it can repeat across ASINs or come back empty).
  const [rejectedDealKeys, setRejectedDealKeys] = useState(new Set());
  // { done, total, failedStores } for the fetch in progress.
  const [progress, setProgress] = useState(null);
  // Bumped by every fetch, so a superseded one stops writing state.
  const requestRef = useRef(0);

  // Shared with the picker so a store offered there always resolves its label
  // and client-ness here.
  const stores = useStores();

  const isAllStores = !store || store === "all";

  const fetchDecisions = useCallback(async () => {
    const request = ++requestRef.current;
    const isCurrent = () => request === requestRef.current;
    setLoading(true);
    setError(null);
    setRemovedExecutionIds(new Set());
    setFionaChanges({});
    setDecisions([]);
    setProductMap({});

    const storesToFetch = isAllStores
      ? stores
          .filter((s) => s.value !== "all")
          .map((s) => ({
            name: s.value.replace("_client", ""),
            label: s.label,
          }))
          .sort(byFetchPriority)
      : [
          {
            name: store.replace("_client", ""),
            label: stores.find((s) => s.value === store)?.label || store,
          },
        ];
    const total = storesToFetch.length;
    const failedStores = [];
    const seen = new Set();
    let collected = [];
    let done = 0;
    setProgress({ done, total, failedStores: [] });

    await forEachWithConcurrency(
      storesToFetch,
      DECISIONS_CONCURRENCY,
      async (s) => {
        let storeDecisions = [];
        try {
          const data = await fetchDecisionsForStore(
            s.name,
            marketplace || "us"
          );
          if (!data.message) {
            storeDecisions = Object.entries(data).flatMap(([asin, items]) => {
              const arr = Array.isArray(items) ? items : [items];
              return arr.map((d) => ({
                ...d,
                asin,
                storeName: s.name,
                storeLabel: s.label,
              }));
            });
          }
        } catch {
          failedStores.push(s.label);
        }
        if (!isCurrent()) return;

        // card_group keeps a Fiona run's today and preview cards apart;
        // card_id names a Dave card, whose execution_id changes every run
        const fresh = storeDecisions.filter((d) => {
          const key = `${d.execution_id || "no-id"}-${d.asin}-${
            d.storeName
          }-${getAgentKey(d)}-${d.decision?.card_group || ""}-${
            d.card_id || ""
          }`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        collected = [...collected, ...fresh].sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
        done += 1;
        setDecisions(collected);
        setProgress({ done, total, failedStores: [...failedStores] });
        setLoading(false);

        const asins = [...new Set(fresh.map((d) => d.asin))];
        if (asins.length > 0) {
          const products = await fetchProductItemsByAsins(asins, s.name);
          if (isCurrent()) setProductMap((prev) => ({ ...prev, ...products }));
        }
      }
    );

    if (!isCurrent()) return;
    setLoading(false);
    if (total > 0 && failedStores.length === total) {
      setError("Failed to fetch recommendations");
    }
  }, [store, isAllStores, stores, marketplace]);

  useEffect(() => {
    if (stores.length > 0) {
      fetchDecisions();
    }
    return () => {
      requestRef.current += 1;
    };
  }, [fetchDecisions, stores]);

  const stillLoading = Boolean(progress && progress.done < progress.total);
  const failedStores = progress?.failedStores ?? [];

  const handleRemoveDecisions = (executionIds) => {
    setRemovedExecutionIds((prev) => new Set([...prev, ...executionIds]));
  };

  const handleDealRejected = (dealKey) => {
    setRejectedDealKeys((prev) => new Set(prev).add(dealKey));
  };

  const handleFionaCardChanged = (decision, change) => {
    setFionaChanges((prev) => {
      if (change.remove === "product") {
        return { ...prev, [fionaProductKey(decision)]: { removed: true } };
      }
      if (change.remove) {
        return { ...prev, [fionaCardKey(decision)]: { removed: true } };
      }
      return {
        ...prev,
        [fionaCardKey(decision)]: { fionaCard: change.fionaCard },
      };
    });
  };

  // The fetched decisions with the Fiona changes applied
  const currentDecisions = React.useMemo(
    () =>
      decisions.flatMap((d) => {
        if (!isFionaDecision(d)) return [d];
        const change = fionaChanges[fionaCardKey(d)];
        if (fionaChanges[fionaProductKey(d)]?.removed || change?.removed) {
          return [];
        }
        return [change ? { ...d, fiona_card: change.fionaCard } : d];
      }),
    [decisions, fionaChanges]
  );

  // TODO: in_progress cards are disabled by now, if not used in a future just delete it
  const isHiddenIrisInProgress = (d) =>
    d.decision?.agent?.toLowerCase() === "iris" &&
    d.automated_execution_status === "in_progress";

  // The cards on screen, each with its tab
  const listedDecisions = currentDecisions.filter(
    (d) =>
      !removedExecutionIds.has(d.execution_id) &&
      !isHiddenIrisInProgress(d) &&
      getRecommendationTab(d) !== null
  );
  const tabCounts = React.useMemo(() => {
    const counts = { open: 0, in_progress: 0, fyi: 0 };
    listedDecisions.forEach((d) => {
      counts[getRecommendationTab(d)] += 1;
    });
    return counts;
  }, [listedDecisions]);
  const tabDecisions = listedDecisions.filter(
    (d) => getRecommendationTab(d) === tab
  );

  // Whether a card shows under an agent's chip
  const matchesAgentFilter = (d, filter) => {
    if (filter === "dave") {
      // Dave's own rows are his "registered a Best Deal" cards. Beyond
      // those, filtering by him shows whichever sensing agent's row is
      // offering an unresolved deal, additive to (not exclusive of) that
      // agent's own filter. Rejecting
      // the deal doesn't pull the row out of this list — the user is
      // usually mid-flow on that same card, switching to the sensing
      // agent's own accept/reject options, so yanking it away here would
      // cut that off. It only stops counting toward the "Dave" badge
      // (see agentCounts below) until the next refetch.
      if (getAgentKey(d) !== "dave" && !decisionHasActiveDeal(d)) return false;
    } else if (filter === "iris" && isRossRevertDecision(d)) {
      // Ross price reverts are executed by Iris — like Dave above, the
      // executing agent's filter is additive to the triggering agent's (Ross).
    } else if (filter !== "all" && getAgentKey(d) !== filter) {
      return false;
    }
    return true;
  };

  const filteredDecisions = tabDecisions.filter((d) =>
    matchesAgentFilter(d, agentFilter)
  );

  // Group Sam decisions by (asin + storeName + keyword) so multiple actions
  // for the same keyword appear in a single card. Non-Sam decisions are kept as-is.
  const groupSamDecisions = (items) => {
    const result = [];
    const samGroups = {};
    items.forEach((d) => {
      if (getAgentKey(d) === "sam") {
        const keyword = d.decision?.keyword || "";
        const groupKey = `${d.asin}|${d.storeName}|${keyword}`;
        if (!samGroups[groupKey]) {
          const group = {
            _isSamGroup: true,
            asin: d.asin,
            storeName: d.storeName,
            storeLabel: d.storeLabel,
            created_at: d.created_at,
            keyword,
            decisions: [],
          };
          samGroups[groupKey] = group;
          result.push(group);
        }
        samGroups[groupKey].decisions.push(d);
      } else {
        result.push(d);
      }
    });
    return result;
  };

  const groupByAsin = (items) => {
    const asinGroups = {};
    items.forEach((item) => {
      const groupKey = `${item.asin}|${item.storeName}`;
      if (!asinGroups[groupKey]) {
        asinGroups[groupKey] = {
          asin: item.asin,
          storeName: item.storeName,
          storeLabel: item.storeLabel,
          sku: item._isSamGroup
            ? item.decisions[0]?.sku
            : item.sku || item.features?.Sku,
          decisions: [],
        };
      }
      asinGroups[groupKey].decisions.push(item);
    });
    const groups = Object.values(asinGroups);
    if (agentFilter !== "fiona") return groups;
    // Products with the biggest yearly saving first, judged by each
    // product's best Fiona card
    const bestFionaCard = (group) =>
      group.decisions
        .filter((d) => !d._isSamGroup && isFionaDecision(d))
        .sort(compareFionaSavings)[0];
    return groups.sort((a, b) =>
      compareFionaSavings(bestFionaCard(a), bestFionaCard(b))
    );
  };

  const groupedFilteredDecisions = React.useMemo(
    () => groupSamDecisions(filteredDecisions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredDecisions]
  );

  const agentCounts = React.useMemo(() => {
    const counts = {};
    const samGroupKeys = new Set();
    // Each agent's chip counts their cards on the current tab
    tabDecisions.forEach((d) => {
      const key = getAgentKey(d);
      if (key === "sam") {
        const groupKey = `${d.asin}|${d.storeName}|${
          d.decision?.keyword || ""
        }`;
        if (!samGroupKeys.has(groupKey)) {
          samGroupKeys.add(groupKey);
          counts.sam = (counts.sam || 0) + 1;
        }
      } else {
        counts[key] = (counts[key] || 0) + 1;
      }
      // Dave's count is additive on top of the sensing agent's own count
      // above, not a replacement for it — a decision can be both "Iris" and
      // "Dave" at once.
      if (decisionHasActiveDeal(d) && !rejectedDealKeys.has(getDealKey(d))) {
        counts.dave = (counts.dave || 0) + 1;
      }
      // Same for Ross price reverts: counted under Ross (trigger) and Iris
      // (executor).
      if (isRossRevertDecision(d)) {
        counts.iris = (counts.iris || 0) + 1;
      }
    });
    return counts;
  }, [tabDecisions, rejectedDealKeys]);

  const allCount = tabDecisions.length;

  const renderAsinCard = (asinGroup) => {
    const { asin, storeName, sku, decisions: asinDecisions } = asinGroup;
    const productInfo = productMap[asin];
    const firstRealDecision = asinDecisions.find((d) => !d._isSamGroup);
    const currentPrice = firstRealDecision?.current_updated_price || null;

    return (
      <Card
        key={`asin-${asin}-${storeName}`}
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: MAX_CARD_WIDTH,
          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
          transition: "box-shadow 0.2s",
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack direction="row" spacing={2}>
            <ProductInfoPanel
              productInfo={productInfo}
              asin={asin}
              storeName={storeName}
              marketplace={marketplace}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MultiDecisionAccordion
                decisions={asinDecisions}
                asin={asin}
                sku={sku}
                store={storeName}
                marketplace={marketplace || "us"}
                currentPrice={currentPrice}
                onRemoveDecision={(executionId) =>
                  handleRemoveDecisions([executionId])
                }
                onDealRejected={handleDealRejected}
                onFionaCardChanged={handleFionaCardChanged}
                sx={{ minWidth: 0, maxWidth: "none", width: "100%" }}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderDecisionsList = (items) => {
    const asinGroups = groupByAsin(items);
    return (
      <Stack spacing={2} sx={{ pl: 6 }}>
        {asinGroups.map((asinGroup) => (
          <React.Fragment key={`asin-${asinGroup.asin}-${asinGroup.storeName}`}>
            {renderAsinCard(asinGroup)}
          </React.Fragment>
        ))}
      </Stack>
    );
  };

  const renderGroupedByStore = (items) => {
    const grouped = {};
    items.forEach((item) => {
      const key = item.storeName;
      if (!grouped[key]) grouped[key] = { label: item.storeLabel, items: [] };
      grouped[key].items.push(item);
    });

    const storeKeys = Object.keys(grouped);

    return (
      <Stack spacing={3}>
        {storeKeys.map((storeKey, idx) => {
          const asinGroups = groupByAsin(grouped[storeKey].items);
          return (
            <Box key={storeKey}>
              {idx > 0 && <Divider sx={{ mb: 3, mt: 1 }} />}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  color: "#333",
                  pl: 6,
                }}
              >
                {grouped[storeKey].label}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1, color: "#999", fontWeight: 400 }}
                >
                  ({asinGroups.length})
                </Typography>
              </Typography>
              <Stack spacing={2} sx={{ pl: 6 }}>
                {asinGroups.map((asinGroup) => (
                  <React.Fragment key={`asin-${asinGroup.asin}-${storeKey}`}>
                    {renderAsinCard(asinGroup)}
                  </React.Fragment>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <img
          src="/assets/custom-suggestion-icon-transparent.png"
          alt="Agents"
          style={{ width: 32, height: 32 }}
        />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Agent Recommendations
        </Typography>
      </Stack>

      {/* Needs decision / In progress / FYI, over every agent's cards */}
      <Tabs
        value={tab}
        onChange={(_e, value) => {
          setTab(value);
          // Keep the agent if they have cards on the new tab, else show all
          const agentHasCards = listedDecisions.some(
            (d) =>
              getRecommendationTab(d) === value &&
              matchesAgentFilter(d, agentFilter)
          );
          if (!agentHasCards) setAgentFilter("all");
        }}
        sx={{
          mb: 2,
          minHeight: 44,
          borderBottom: "1px solid #e0e0e0",
          "& .MuiTab-root": { minHeight: 44 },
        }}
      >
        {RECOMMENDATION_TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {`${t.label} `}
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    borderRadius: 2.5,
                    px: 1,
                    py: "1px",
                    backgroundColor: tab === t.key ? "#376fd0" : "#eceff3",
                    color: tab === t.key ? "#fff" : "#4b5563",
                  }}
                >
                  {tabCounts[t.key]}
                </Box>
              </Box>
            }
            sx={{ textTransform: "none", fontWeight: 500 }}
          />
        ))}
      </Tabs>

      {/* Agent filter chips */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3, gap: 1 }}>
        <Chip
          label={`All (${allCount})`}
          variant={agentFilter === "all" ? "filled" : "outlined"}
          color={agentFilter === "all" ? "primary" : "default"}
          onClick={() => setAgentFilter("all")}
          sx={{ fontWeight: 500 }}
        />
        {Object.entries(AGENT_INFO).map(([key, info]) => {
          const count = agentCounts[key] || 0;
          // An agent with nothing on this tab drops out, unless selected
          if (count === 0 && agentFilter !== key) return null;
          return (
            <Chip
              key={key}
              avatar={
                <Avatar src={info.avatar} sx={{ width: 24, height: 24 }} />
              }
              label={`${info.name} (${count})`}
              variant={agentFilter === key ? "filled" : "outlined"}
              onClick={() => setAgentFilter(key)}
              sx={{
                fontWeight: 500,
                borderColor: agentFilter === key ? info.color : undefined,
                backgroundColor:
                  agentFilter === key ? `${info.color}18` : undefined,
                color: agentFilter === key ? info.color : undefined,
              }}
            />
          );
        })}
      </Stack>

      {/* Content */}
      {!loading && !error && (stillLoading || failedStores.length > 0) && (
        <Box sx={{ mb: 2 }}>
          {stillLoading && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Loaded {progress.done} of {progress.total} stores…
            </Typography>
          )}
          {failedStores.length > 0 && (
            <Typography variant="caption" color="error" display="block">
              Could not load recommendations for {failedStores.join(", ")}.
            </Typography>
          )}
        </Box>
      )}

      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && groupedFilteredDecisions.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
          sx={{ color: "#999" }}
        >
          <img
            src="/assets/custom-suggestion-icon-transparent.png"
            alt="No recommendations"
            style={{ width: 48, height: 48, opacity: 0.4, marginBottom: 8 }}
          />
          <Typography variant="h6">No recommendations</Typography>
          <Typography variant="body2">
            {stillLoading
              ? "No recommendations in the stores loaded so far."
              : isAllStores
              ? "No active agent recommendations across stores."
              : "No active agent recommendations for this store."}
          </Typography>
        </Box>
      )}

      {!loading &&
        !error &&
        groupedFilteredDecisions.length > 0 &&
        (isAllStores
          ? renderGroupedByStore(groupedFilteredDecisions)
          : renderDecisionsList(groupedFilteredDecisions))}
    </Box>
  );
}
